import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import type { Patient } from '../patient/patient';
import type { Image3D } from '../patient/image3d';
import type { RigidRegistration } from '../patient/rigidRegistration';
import type { StructureSet } from '../patient/structureset';
import type {
    ConnectorImageManifestEntry,
    ConnectorPatientManifest,
    ConnectorRegistrationManifestEntry,
    ConnectorStructureSetManifestEntry,
} from './ConnectorPatientManifest';
import {
    asConnectorDatasetId,
    asConnectorPatientId,
    asConnectorVersionToken,
} from './PersistenceRef';
import { PersistenceRegistry } from './PersistenceRegistry';

/**
 * Matches the parsed domain aggregate against the connector manifest
 * captured on the worklist, producing the session's persistence registry.
 *
 * Matching is deliberately conservative — a wrong match would save one
 * structure set's content over another database row:
 *
 * 1. SOP Instance UID is the primary key (unique per stored RTSTRUCT).
 * 2. Series UID is a fallback used only when it is unambiguous on BOTH
 *    sides (exactly one unmatched manifest entry and exactly one unmatched
 *    domain set carry it) — two structure sets can legally share a series.
 * 3. A manifest whose DICOM PatientID differs from the parsed patient is
 *    refused wholesale: nothing attaches, the session is "local only" and
 *    saving stays unavailable. Fail safe over fail functional.
 *
 * Unmatched domain datasets simply get no ref ("local only").
 *
 * Registrations and doses match by SOP Instance UID only (no series
 * fallback — deliberately more conservative than structure sets); images
 * match by Series Instance UID, each manifest entry claimed at most once.
 *
 * Matched structure sets additionally adopt the row's approval state: the
 * approval endpoint updates only the database row, never the stored
 * RTSTRUCT file, so after an approval-only save the file's tag is stale.
 * They adopt the row's per-ROI edit locks on the same grounds, and there the
 * row is the only carrier at all — DICOM has no lock attribute.
 * Matched registrations adopt theirs too — there the row is the ONLY
 * carrier (the DICOM Spatial Registration IOD has no approval attribute).
 * Both overlays run before the tracker attaches and before pending-change
 * baselines are captured, so they are load-time truth, not pending edits.
 */
export function hydratePersistenceRefs(
    patient: Patient,
    manifest: ConnectorPatientManifest | null | undefined
): PersistenceRegistry {
    if (!manifest) {
        return new PersistenceRegistry(null);
    }
    if (manifest.dicomPatientId !== patient.patientId) {
        // Deliberately no identifiers in the message — PatientID is PHI.
        console.warn(
            '[persistence] connector manifest does not match the loaded ' +
                'patient; database saving is disabled for this session'
        );
        return new PersistenceRegistry(null);
    }

    const registry = new PersistenceRegistry({
        patientId: asConnectorPatientId(manifest.patientId),
        dicomPatientId: manifest.dicomPatientId,
        version: asConnectorVersionToken(manifest.version),
    });

    const claimed = new Set<ConnectorStructureSetManifestEntry>();

    // Pass 1: SOP Instance UID.
    const bySop = new Map<string, ConnectorStructureSetManifestEntry>();
    for (const entry of manifest.structureSets) {
        if (entry.sopInstanceUid !== null) {
            bySop.set(entry.sopInstanceUid, entry);
        }
    }
    for (const ss of patient.structureSets) {
        const entry = bySop.get(ss.dicomUid);
        if (entry && !claimed.has(entry)) {
            registry.setRef(ss.id, {
                datasetId: asConnectorDatasetId(entry.datasetId),
                version: asConnectorVersionToken(entry.version),
            });
            claimed.add(entry);
            adoptStructureSetRow(ss, entry);
        }
    }

    // Pass 2: unique-series fallback for what pass 1 left unmatched.
    const unmatchedSets = patient.structureSets.filter(
        (ss) => registry.getRef(ss.id) === null
    );
    for (const ss of unmatchedSets) {
        const candidates = manifest.structureSets.filter(
            (entry) =>
                !claimed.has(entry) &&
                entry.seriesInstanceUid === ss.seriesInstanceUid
        );
        const peers = unmatchedSets.filter(
            (other) => other.seriesInstanceUid === ss.seriesInstanceUid
        );
        if (candidates.length === 1 && peers.length === 1) {
            registry.setRef(ss.id, {
                datasetId: asConnectorDatasetId(candidates[0].datasetId),
                version: asConnectorVersionToken(candidates[0].version),
            });
            claimed.add(candidates[0]);
            adoptStructureSetRow(ss, candidates[0]);
        }
    }

    // Registrations: SOP Instance UID only.
    const regBySop = new Map<string, ConnectorRegistrationManifestEntry>();
    for (const entry of manifest.registrations) {
        if (entry.sopInstanceUid !== null) {
            regBySop.set(entry.sopInstanceUid, entry);
        }
    }
    const claimedReg = new Set<ConnectorRegistrationManifestEntry>();
    for (const reg of patient.registrations) {
        const entry = regBySop.get(reg.sopInstanceUid);
        if (entry && !claimedReg.has(entry)) {
            registry.setRef(reg.id, {
                datasetId: asConnectorDatasetId(entry.datasetId),
                version: asConnectorVersionToken(entry.version),
            });
            claimedReg.add(entry);
            // Label before approval: the label write must never trip the
            // approval guard of an already-adopted approved state.
            adoptRegistrationRowLabel(reg, entry);
            adoptRegistrationRowApproval(reg, entry);
        }
    }

    // Doses: SOP Instance UID only.
    const doseBySop = new Map<string, string[]>();
    for (const entry of manifest.doses) {
        if (entry.sopInstanceUid !== null) {
            doseBySop.set(entry.sopInstanceUid, [
                entry.datasetId,
                entry.version,
            ]);
        }
    }
    const claimedDoseSops = new Set<string>();
    for (const dose of patient.doses) {
        const entry = doseBySop.get(dose.sopInstanceUid);
        if (entry && !claimedDoseSops.has(dose.sopInstanceUid)) {
            registry.setRef(dose.id, {
                datasetId: asConnectorDatasetId(entry[0]),
                version: asConnectorVersionToken(entry[1]),
            });
            claimedDoseSops.add(dose.sopInstanceUid);
        }
    }

    // Images: Series Instance UID (an image IS its series), claimed once.
    const imageBySeries = new Map<string, ConnectorImageManifestEntry>();
    for (const entry of manifest.images) {
        imageBySeries.set(entry.seriesInstanceUid, entry);
    }
    const claimedImageSeries = new Set<string>();
    for (const image of patient.images) {
        const entry = imageBySeries.get(image.seriesUid);
        if (entry && !claimedImageSeries.has(image.seriesUid)) {
            registry.setRef(image.id, {
                datasetId: asConnectorDatasetId(entry.datasetId),
                version: asConnectorVersionToken(entry.version),
            });
            claimedImageSeries.add(image.seriesUid);
            adoptImageRowLabel(image, entry);
        }
    }

    return registry;
}

/**
 * Overlays the database row's display label onto the parsed image. The row
 * is display-authoritative after a rename — the stored slice files keep
 * their original tag — but only a non-empty, different value overlays, so an
 * older backend or an untouched row keeps the parsed metadata.
 */
export function adoptImageRowLabel(
    image: Image3D,
    entry: ConnectorImageManifestEntry
): void {
    if (
        entry.seriesDescription &&
        entry.seriesDescription !== image.seriesMetadata.seriesDescription
    ) {
        image.adoptSeriesDescription(entry.seriesDescription);
    }
}

/** Maps the row's wire approval onto the domain enum; null = unknown. */
function toDomainApproval(status: string | null): DICOMApprovalStatus | null {
    switch (status) {
        case 'Approved':
            return DICOMApprovalStatus.APPROVED;
        case 'Rejected':
            return DICOMApprovalStatus.REJECTED;
        case 'Unapproved':
            return DICOMApprovalStatus.UNAPPROVED;
        default:
            return null;
    }
}

/**
 * Overlays everything the database row carries that the parsed RTSTRUCT file
 * cannot: its authoritative approval, the per-ROI reviews and edit locks, the
 * connector's record of how the set came to exist, and which structures the AI
 * model produced and whether their contours have been edited since. A set is
 * adopted the same way whether it was loaded at open or on demand later.
 */
export function adoptStructureSetRow(
    ss: StructureSet,
    entry: ConnectorStructureSetManifestEntry
): void {
    adoptStructureSetRowApproval(ss, entry);
    adoptRowRoiReviews(ss, entry);
    adoptRowEditLocks(ss, entry);
    ss.adoptRecordedOrigin(entry.origin);
    const modelGenerated = new Set(entry.modelGeneratedRoiNumbers);
    const contourEdited = new Set(entry.contourEditedRoiNumbers);
    for (const roi of ss.rois) {
        roi.adoptModelGeneration(
            modelGenerated.has(roi.roiNumber),
            contourEdited.has(roi.roiNumber)
        );
    }
}

/**
 * Overlays the database row's authoritative approval onto the parsed
 * structure set (cascading to its ROIs, mirroring a user transition).
 * An unknown/absent row status keeps the file's value — an older backend
 * must not silently unapprove everything.
 */
function adoptStructureSetRowApproval(
    ss: StructureSet,
    entry: ConnectorStructureSetManifestEntry
): void {
    const rowStatus = toDomainApproval(entry.approvalStatus);
    if (rowStatus === null) {
        return;
    }
    const rowReviewer = entry.reviewerName ?? '';
    if (ss.approvalStatus === rowStatus && ss.reviewerName === rowReviewer) {
        return;
    }
    ss.setApprovalStatus(rowStatus, rowReviewer, true);
}

/**
 * Overlays the row's per-ROI reviews onto the parsed structure set. Runs after
 * the set approval, whose cascade rewrites every ROI: on an approved or
 * rejected set that decision covers every structure and the per-ROI rows are
 * not consulted. The status is written through the domain ROI like a user
 * review, and re-baselined by the caller like the locks below; the reviewer
 * and time come with it.
 */
function adoptRowRoiReviews(
    ss: StructureSet,
    entry: ConnectorStructureSetManifestEntry
): void {
    if (ss.approvalStatus !== DICOMApprovalStatus.UNAPPROVED) {
        return;
    }
    const reviews = new Map(
        entry.roiReviews.map((review) => [review.roiNumber, review])
    );
    for (const roi of ss.rois) {
        const status = reviews.get(roi.roiNumber)?.status;
        if (status === DICOMApprovalStatus.APPROVED) {
            roi.approve('');
        } else if (status === DICOMApprovalStatus.REJECTED) {
            roi.reject('');
        } else {
            roi.unapprove();
        }
    }
    ss.adoptRecordedRoiReviews(entry.roiReviews);
}

/**
 * Overlays the row's per-ROI edit locks onto the parsed structure set. The row
 * is the only carrier — DICOM RT Structure Set defines no lock attribute — so
 * without this a reopened patient shows every structure unlocked. Written
 * through the domain ROI, which reports the change to its tracker when it has
 * one: a set that is already attached must be overlaid inside a tracker
 * batch, and re-baselined afterwards so the locks read as load-time truth
 * rather than an unsaved edit.
 */
function adoptRowEditLocks(
    ss: StructureSet,
    entry: ConnectorStructureSetManifestEntry
): void {
    const locked = new Set(entry.lockedRoiNumbers);
    for (const roi of ss.rois) {
        // Set unconditionally, not only for the locked ones: the parsed file
        // never carries a lock, so a false here is a no-op, and reading the row
        // as the whole truth keeps the two in step if that ever changes.
        roi.setLocked(locked.has(roi.roiNumber));
    }
}

/**
 * Overlays the database row's display label onto the parsed registration.
 * Server-written REG files carry the generic ContentLabel "REGISTRATION"
 * (CS16) with the real label in SeriesDescription, and the rename PATCH
 * updates only the row — so the row's label is what the user last chose.
 * Writes through the dicom parent directly (no guard, no tracker): this
 * is load-time truth, and it must not trip the approval lock. The CS16
 * setter truncation applies, same as the rename UI. A missing/empty row
 * label keeps the parsed value.
 */
export function adoptRegistrationRowLabel(
    reg: RigidRegistration,
    entry: ConnectorRegistrationManifestEntry
): void {
    if (!entry.seriesDescription) {
        return;
    }
    if (reg.contentLabel === entry.seriesDescription) {
        return;
    }
    // Through the LO-wide path: the row's name is LO (64) but ContentLabel is
    // CS (16), so writing it straight to the tag truncated any legal name
    // longer than that — and the guard above, comparing against the truncated
    // getter, then re-ran the adoption on every load.
    reg.adoptPersistedLabel(entry.seriesDescription);
}

/**
 * Overlays the database row's approval onto the parsed registration. The
 * row is the ONLY carrier of registration approval (the DICOM Spatial
 * Registration IOD has no approval attribute), so an imported REG always
 * arrives Unapproved in memory and this is where its saved state returns.
 * An absent/unknown row status keeps the in-memory default — an older
 * backend must not be mistaken for an unapproval.
 */
export function adoptRegistrationRowApproval(
    reg: RigidRegistration,
    entry: ConnectorRegistrationManifestEntry
): void {
    const rowStatus = toDomainApproval(entry.approvalStatus);
    if (rowStatus === null) {
        return;
    }
    const rowReviewer = entry.reviewerName ?? '';
    if (reg.approvalStatus === rowStatus && reg.reviewerName === rowReviewer) {
        return;
    }
    switch (rowStatus) {
        case DICOMApprovalStatus.APPROVED:
            reg.approve(rowReviewer);
            break;
        case DICOMApprovalStatus.REJECTED:
            reg.reject(rowReviewer);
            break;
        default:
            reg.unapprove();
            break;
    }
}
